import React, { useCallback, useEffect, useMemo } from 'react';

import { AlineTransforms, PermissionRole } from '@aline/common';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { Grid } from '@mui/material';
import { useAsync } from 'react-use';
import type { Editor } from 'slate';
import {
  ComponentLifecycleCallbacks,
  ForwardDocumentDialog,
  ShareDocumentDialog,
  ShareDocumentViaEmailDialog,
  UploadImageFilesPicker,
  useInstantiateFileViewer,
  WorkflowStepsDialog,
} from 'src/components';
import { CitationParamDeepLink } from 'src/components/ai/shared/components/useCitationNavigation';
import { EditorRuntimeProvider } from 'src/editor/editor-runtime-registry';
import {
  useEditorTabStateActionsForTabId,
  useEditorTabStateSelectorForTabId,
} from 'src/editor/editor-tab-state-registry';
import { EditorBody } from 'src/editor/EditorBody';
import {
  ExportDocumentDialogContainer,
  UnsupportedTrackedChangesDialog,
  useWalkthroughActive,
} from 'src/editor/toolbars';
import { EditorAppBar } from 'src/editor/toolbars/editor-app-bars/EditorAppBar';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import type { AfterImageUploadDoneResult } from 'src/helpers';
import { FileStackDomContainer, useDispatchNotification, useDocumentParams } from 'src/helpers';
import { useDocumentReconnectSnackbar } from 'src/helpers/connection-status';
import { editorImageMimeTypes } from 'src/helpers/integrations/filestack';
import {
  useCannedFieldsForDoc,
  useCleanupOrphanCannedFieldsInSlate,
  useGetCurrentDoc,
  useGetEditorUserState,
  useRefetchCannedFieldsOnDocumentUpdated,
  useSyncCannedFieldValuesToSlate,
} from 'src/hooks';
import { SignatureDialog, Slate } from 'src/layout-engine';
import { useCurrentTabIdOrThrow } from 'src/layouts/tabs/tab-active-context';
import { makeStyles } from 'tss-react/mui';

import { AnnounceEditor } from './AnnounceEditor';
import { EditorErrorBoundary } from './EditorErrorBoundary';
import { useLoadCollaborativeDocAndFonts } from './helpers/useLoadDoc';
import { usePlaywrightIfEnabled } from './helpers/usePlaywright';
import { SpellcheckRunner } from './SpellcheckRunner';

export const shareDialogOpenVar = makeVar<boolean>(false);
export const imageDialogStateVar = makeVar<{ open: boolean; initialImage?: Blob }>({ open: false });

const useStyles = makeStyles()(() => ({
  mainGrid: {
    flex: '1 1 0%',
    minHeight: 0,
    minWidth: 0,
    position: 'relative',
  },
}));

export const AlineEditor = React.memo(function AlineEditor(): JSX.Element | null {
  const { classes } = useStyles();
  const tabId = useCurrentTabIdOrThrow();
  const {
    documentUi: { setDisconnected },
  } = useEditorTabStateActionsForTabId(tabId);
  const { document } = useGetCurrentDoc();
  const { documentId } = useDocumentParams();
  const { disposeFileViewer, documentViewer } = useInstantiateFileViewer();
  const imageDialogState = useReactiveVar(imageDialogStateVar);
  const shareDialogOpen = useReactiveVar(shareDialogOpenVar);
  const [setEnvelopeViewed] = Apollo.useSetEnvelopeViewedMutation();
  const { connectProvider, connected, destroyProvider, editor, editorLoading } =
    useLoadCollaborativeDocAndFonts();
  const { wasSentForSignature, workflowId } = useCannedFieldsForDoc(documentId);
  const editingLocked = useDocumentReconnectSnackbar({ connected, loading: editorLoading });
  const [addToRecentlyAccessedDocuments] = Apollo.useAddToRecentlyAccessedDocumentsMutation();
  const { afterUploadImage } = useAfterUploadImage(editor);
  const handleUnmount = useCallback(() => {
    destroyProvider();
    disposeFileViewer();
  }, [destroyProvider, disposeFileViewer]);
  const runtime = useMemo(() => {
    return {
      documentId,
      documentViewer,
      editor,
      editorLoading,
      tabId,
    };
  }, [documentId, documentViewer, editor, editorLoading, tabId]);

  usePlaywrightIfEnabled(editor);
  useRefetchCannedFieldsOnDocumentUpdated(documentId);
  useCleanupOrphanCannedFieldsInSlate(editor, workflowId, editorLoading);
  useSyncCannedFieldValuesToSlate(editor, workflowId, wasSentForSignature, editorLoading);
  useAsync(async () => {
    await addToRecentlyAccessedDocuments({ variables: { documentId } });
  }, [documentId]);

  useEffect(() => {
    setDisconnected(connected);
  }, [connected, setDisconnected]);

  useAsync(async () => {
    if (document?.workflow?.id)
      await setEnvelopeViewed({ variables: { workflowId: document.workflow.id } });
  }, [document?.workflow?.id]);

  return (
    <EditorRuntimeProvider runtime={runtime}>
      <CitationParamDeepLink />
      <EditorErrorBoundary editor={editor}>
        <Slate editor={editor} editorLoading={editorLoading} isReadOnly={editingLocked}>
          <Grid
            container
            className={classes.mainGrid}
            sx={{ flexDirection: 'column' }}
            wrap="nowrap"
          >
            <EditorAppBar />
            <EditorBody />
          </Grid>
          <SignatureDialog />
          <ForwardDocumentDialog />
          <ShareDocumentViaEmailDialog />
          <WorkflowStepsDialog />
          <ShareDocumentDialog
            documentId={documentId}
            handleClose={() => shareDialogOpenVar(false)}
            open={shareDialogOpen}
          />
          <UnsupportedTrackedChangesDialog />
          <ExportDocumentDialogContainer />
          <UploadImageFilesPicker
            domContainer={FileStackDomContainer.Image}
            open={imageDialogState.open}
            pickerOpts={{
              accept: [...editorImageMimeTypes],
              afterUploadDone: afterUploadImage,
              initialFiles: imageDialogState.initialImage
                ? [imageDialogState.initialImage]
                : undefined,
              maxFiles: 1,
              onClose: () => imageDialogStateVar({ open: false }),
            }}
          />
          <AnnounceEditor />
          <SpellcheckRunner />
        </Slate>
      </EditorErrorBoundary>
      <ComponentLifecycleCallbacks onMount={connectProvider} onUnmount={handleUnmount} />
    </EditorRuntimeProvider>
  );
});

/**
 * Publishes the user's editing permissions (PermissionRole, and the EditingMode they resolve to)
 * for the current document. The mode the user picked survives a re-sync with unchanged
 * permissions, so running this again when a tab pane is shown changes nothing.
 */
export function useSetUserEditingPermissions() {
  const tabId = useCurrentTabIdOrThrow();
  const {
    documentUi: { syncEditingPermissions },
  } = useEditorTabStateActionsForTabId(tabId);
  const { document } = useGetCurrentDoc();
  const currentUserId = useCurrentUserId();
  const { walkThroughActive } = useWalkthroughActive();
  const { isInSigningCeremony, isReadOnly } = useGetEditorUserState();
  const setUserEditingPermissions = useCallback(() => {
    syncEditingPermissions({
      forceViewing: isReadOnly || isInSigningCeremony || walkThroughActive,
      // If the user isn't logged in (no currentUserId), coerce to PermissionRole.Viewer until the user logs in.
      permissionRole: !currentUserId ? PermissionRole.Viewer : (document?.requesterRole ?? null),
    });
  }, [
    currentUserId,
    document?.requesterRole,
    isInSigningCeremony,
    isReadOnly,
    syncEditingPermissions,
    walkThroughActive,
  ]);

  return { setUserEditingPermissions };
}

export function useAfterUploadImage(editor: Editor) {
  const tabId = useCurrentTabIdOrThrow();
  const currentUserId = useCurrentUserId();
  const dispatchErrorNotification = useDispatchNotification({
    message: 'Failed to add the image. Please try again.',
    variant: 'error',
  });
  const editingMode = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.documentUi.editingMode
  );
  const afterUploadImage = useCallback(
    (res: AfterImageUploadDoneResult[]): void => {
      // We only allow uploading one image at a time...
      const response = res.at(0);
      if (!response || !editingMode || !currentUserId) return dispatchErrorNotification();

      AlineTransforms.insertDrawing(editor, {
        authorId: currentUserId,
        editingMode,
        props: {
          drawingImgId: response.id,
          imgHeightPx: response.height,
          imgWidthPx: response.width,
        },
      });
      ClientTransforms.focus(editor);
    },
    [currentUserId, dispatchErrorNotification, editingMode, editor]
  );

  return { afterUploadImage };
}
